export default function Guide({ onLogin }) {
  const steps = [
    {
      num: "01",
      title: "Pick your niche",
      desc: "Choose from 15 built-in niches like Personal Finance, Health & Wellness, or Digital Marketing — or type your own. The more specific, the better your results.",
      visual: (
        <div style={{ background: "#f7f7f5", borderRadius: 10, padding: 20, border: "1px solid #e8e8e4" }}>
          <div style={{ fontSize: 11, color: "#3b5bdb", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12 }}>Find PDF Topics</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <div style={{ fontSize: 11, color: "#aaa", marginBottom: 5 }}>Pick a niche</div>
              <div style={{ background: "#fff", border: "2px solid #3b5bdb", borderRadius: 8, padding: "9px 12px", fontSize: 13, color: "#1a1a1a" }}>Personal Finance & Investing ▾</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: "#aaa", marginBottom: 5 }}>Or search a topic</div>
              <div style={{ background: "#fff", border: "1px solid #e8e8e4", borderRadius: 8, padding: "9px 12px", fontSize: 13, color: "#aaa" }}>e.g. passive income…</div>
            </div>
          </div>
          <div style={{ marginTop: 12, background: "#3b5bdb", color: "#fff", borderRadius: 8, padding: "11px", textAlign: "center", fontSize: 14, fontWeight: 600 }}>Generate 10 Topics</div>
        </div>
      ),
    },
    {
      num: "02",
      title: "Get 10 scored topics instantly",
      desc: "We generate 10 curated PDF topic ideas and score each one for sellability. Every topic shows a score from 65–98, colour-coded green to amber. Topics scoring 85+ get a 🔥 Write This badge — those are your best bets.",
      visual: (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {[
            { title: "The Passive Income Playbook: 7 Streams Anyone Can Build", score: 91, hot: true, virality: "Explosive", comp: "Low" },
            { title: "Credit Score Secrets: From 580 to 750 in 6 Months", score: 78, hot: false, virality: "High", comp: "Moderate" },
            { title: "Budgeting for Beginners: Zero to Savings in 30 Days", score: 72, hot: false, virality: "High", comp: "Competitive" },
          ].map((t, i) => (
            <div key={i} style={{ background: "#fff", border: "1px solid #e8e8e4", borderRadius: 10, padding: "14px 16px", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#1a1a1a", marginBottom: 8, lineHeight: 1.4 }}>{t.title}</div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  <span style={{ background: "#3b5bdb10", border: "1px solid #3b5bdb25", borderRadius: 20, padding: "2px 8px", fontSize: 11, color: "#3b5bdb" }}>Virality: {t.virality}</span>
                  <span style={{ background: "#0a7c4e10", border: "1px solid #0a7c4e25", borderRadius: 20, padding: "2px 8px", fontSize: 11, color: "#0a7c4e" }}>Competition: {t.comp}</span>
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, flexShrink: 0 }}>
                {t.hot && <div style={{ background: "#065f46", color: "#fff", fontSize: 9, fontWeight: 700, borderRadius: 20, padding: "2px 8px" }}>🔥 WRITE THIS</div>}
                <div style={{ background: t.score >= 85 ? "#d1fae5" : t.score >= 70 ? "#ecfdf5" : "#fffbeb", border: `1px solid ${t.score >= 85 ? "#6ee7b7" : t.score >= 70 ? "#a7f3d0" : "#fde68a"}`, borderRadius: 8, padding: "6px 10px", textAlign: "center", minWidth: 60 }}>
                  <div style={{ fontSize: 18, fontWeight: 800, color: t.score >= 85 ? "#065f46" : t.score >= 70 ? "#0a7c4e" : "#b45309" }}>{t.score}</div>
                  <div style={{ fontSize: 8, color: "#aaa", fontWeight: 600, textTransform: "uppercase" }}>Sellability</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ),
    },
    {
      num: "03",
      title: "Dig into the full insight",
      desc: "Click 'Show insight' on any topic to reveal the full market analysis — a description, target audience, virality breakdown, competition detail, and individual dimension scores. All generated in seconds.",
      visual: (
        <div style={{ background: "#fff", border: "1px solid #e8e8e4", borderRadius: 10, padding: 16 }}>
          <div style={{ fontSize: 13, color: "#444", lineHeight: 1.7, marginBottom: 12 }}>A step-by-step PDF for first-time investors explaining index funds, emergency funds, and compound interest without the jargon.</div>
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 10, color: "#999", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>Target Audience</div>
            <div style={{ fontSize: 13, color: "#333", fontWeight: 500 }}>20–35 year olds, Recent graduates, First-time earners</div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
            {[{ label: "Virality", val: "Explosive", color: "#3b5bdb" }, { label: "Competition", val: "Low", color: "#0a7c4e" }, { label: "Market", val: "Millennials", color: "#3b5bdb" }].map((s, i) => (
              <div key={i} style={{ background: "#f7f7f5", borderRadius: 8, padding: "10px 10px" }}>
                <div style={{ fontSize: 9, color: "#aaa", fontWeight: 700, textTransform: "uppercase", marginBottom: 3 }}>{s.label}</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: s.color }}>{s.val}</div>
              </div>
            ))}
          </div>
        </div>
      ),
    },
    {
      num: "04",
      title: "Generate a full PDF outline",
      desc: "Hit 'Generate PDF Outline' and we'll produce a full chapter-by-chapter outline for that topic — with sub-points for each chapter, ready to hand to a writer or start writing yourself.",
      visual: (
        <div style={{ background: "#f7f7f5", borderRadius: 10, padding: 16, border: "1px solid #e8e8e4" }}>
          <div style={{ fontSize: 10, color: "#3b5bdb", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>Chapter Outline</div>
          {[
            { ch: "Chapter 1", title: "Why Most People Stay Broke", points: ["The psychology of avoidance", "Common money myths debunked", "The real cost of waiting"] },
            { ch: "Chapter 2", title: "Your First £1,000 Emergency Fund", points: ["Why this number first", "The 30-day sprint method", "Where to keep it"] },
          ].map((c, i) => (
            <div key={i} style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#1a1a1a", marginBottom: 4 }}>{c.ch}: {c.title}</div>
              {c.points.map((p, j) => <div key={j} style={{ fontSize: 12, color: "#666", paddingLeft: 12, lineHeight: 1.8 }}>• {p}</div>)}
            </div>
          ))}
          <div style={{ fontSize: 11, color: "#aaa", fontStyle: "italic" }}>+ 6 more chapters…</div>
        </div>
      ),
    },
    {
      num: "05",
      title: "Save your favourites",
      desc: "Star any topic to save it. Your favourites sync across devices and sessions. When you're ready, export them all as a CSV with scores included — perfect for sharing with a team or tracking your pipeline.",
      visual: (
        <div style={{ background: "#fff", border: "1px solid #e8e8e4", borderRadius: 10, padding: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#1a1a1a" }}>★ Saved (3)</div>
            <div style={{ background: "transparent", border: "1px solid #e8e8e4", borderRadius: 8, padding: "5px 12px", fontSize: 12, color: "#666" }}>⬇ Export CSV</div>
          </div>
          {["The Passive Income Playbook", "Credit Score Secrets", "Budgeting for Beginners"].map((t, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: i < 2 ? "1px solid #f0f0ee" : "none" }}>
              <div style={{ fontSize: 13, color: "#333" }}>{t}</div>
              <span style={{ color: "#e8590c", fontSize: 16 }}>★</span>
            </div>
          ))}
        </div>
      ),
    },
  ];

  return (
    <div style={{ minHeight: "100vh", background: "#f7f7f5", fontFamily: "Inter, sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }

        /* Step layout: 2 columns on desktop, single column on mobile.
           We also kill the RTL alternation on mobile — it only makes sense
           in a 2-column layout. */
        .guide-step {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 48px;
          align-items: center;
          margin-bottom: 80px;
        }
        .guide-step.alt { direction: rtl; }
        .guide-step > * { direction: ltr; }

        /* Mobile-only tweaks. 720px is a reasonable breakpoint — below this
           the 2-column step layout starts squeezing text into ribbons. */
        @media (max-width: 720px) {
          .guide-step,
          .guide-step.alt {
            grid-template-columns: 1fr;
            direction: ltr;
            gap: 24px;
            margin-bottom: 56px;
          }
          .guide-nav-tag { display: none !important; }
          .guide-nav-cta { padding: 8px 14px !important; font-size: 13px !important; }
          .guide-cta-card { padding: 36px 20px !important; }
          .guide-cta-card h2 { font-size: 24px !important; }
        }
      `}</style>

      {/* Nav */}
      <nav style={{ background: "#fff", borderBottom: "1px solid #e8e8e4", padding: "14px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 100, gap: 12 }}>
        <button onClick={() => window.location.href = "/"} style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 6, padding: 0, minWidth: 0 }}>
          <span style={{ fontSize: 18, fontWeight: 800, color: "#1a1a1a" }}>PDFTopicScout</span>
          <span className="guide-nav-tag" style={{ fontSize: 12, color: "#3b5bdb", fontWeight: 600 }}>by Creatovate</span>
        </button>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          <button onClick={() => window.location.href = "/pricing"} className="guide-nav-cta" style={{ background: "#3b5bdb", color: "#fff", border: "none", borderRadius: 8, padding: "9px 20px", fontWeight: 600, fontSize: 14, cursor: "pointer", fontFamily: "Inter, sans-serif" }}>Get Started</button>
          <button onClick={onLogin} className="guide-nav-cta" style={{ background: "transparent", color: "#555", border: "1px solid #e8e8e4", borderRadius: 8, padding: "9px 18px", fontWeight: 600, fontSize: 14, cursor: "pointer", fontFamily: "Inter, sans-serif" }}>Sign In</button>
        </div>
      </nav>

      {/* Header */}
      <div style={{ textAlign: "center", padding: "60px 24px 20px" }}>
        <h1 style={{ fontSize: "clamp(28px, 5vw, 46px)", fontWeight: 900, color: "#1a1a1a", marginBottom: 12, letterSpacing: "-0.5px" }}>How to use PDFTopicScout</h1>
        <p style={{ fontSize: 17, color: "#666", maxWidth: 500, margin: "0 auto" }}>From zero to validated PDF topic in under 2 minutes. Here's exactly how it works.</p>
      </div>

      {/* Steps */}
      <div style={{ maxWidth: 840, margin: "0 auto", padding: "40px 20px 80px" }}>
        {steps.map((step, i) => (
          <div key={i} className={"guide-step" + (i % 2 !== 0 ? " alt" : "")}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                <div style={{ width: 40, height: 40, background: "#3b5bdb", color: "#fff", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 800, flexShrink: 0 }}>{step.num}</div>
                <h2 style={{ fontSize: 20, fontWeight: 800, color: "#1a1a1a" }}>{step.title}</h2>
              </div>
              <p style={{ fontSize: 15, color: "#555", lineHeight: 1.8 }}>{step.desc}</p>
            </div>
            <div>{step.visual}</div>
          </div>
        ))}

        {/* CTA */}
        <div className="guide-cta-card" style={{ background: "#0f172a", borderRadius: 16, padding: "48px 32px", textAlign: "center", color: "#fff" }}>
          <h2 style={{ fontSize: 28, fontWeight: 800, marginBottom: 10 }}>Ready to find your next PDF topic?</h2>
          <p style={{ color: "#94a3b8", fontSize: 15, marginBottom: 28 }}>Plans start at $7.99/month. Cancel any time.</p>
          <button onClick={() => window.location.href = "/pricing"} style={{ background: "#3b5bdb", color: "#fff", border: "none", borderRadius: 12, padding: "16px 36px", fontWeight: 700, fontSize: 16, cursor: "pointer", fontFamily: "Inter, sans-serif" }}>
            See Plans →
          </button>
        </div>
      </div>

      {/* Footer */}
      <div style={{ borderTop: "1px solid #e8e8e4", padding: "20px 28px", background: "#fff", textAlign: "center" }}>
        <div style={{ fontSize: 13, color: "#999", marginBottom: 8 }}>
          <strong style={{ color: "#1a1a1a" }}>PDFTopicScout</strong> by <strong style={{ color: "#3b5bdb" }}>Creatovate</strong>
        </div>
        <div style={{ display: "flex", gap: 16, justifyContent: "center" }}>
          <a href="/privacy" style={{ fontSize: 12, color: "#aaa", textDecoration: "none" }}>Privacy Policy</a>
          <span style={{ color: "#ddd" }}>·</span>
          <a href="/terms" style={{ fontSize: 12, color: "#aaa", textDecoration: "none" }}>Terms of Service</a>
          <span style={{ color: "#ddd" }}>·</span>
          <a href="mailto:info@creatovate.biz" style={{ fontSize: 12, color: "#aaa", textDecoration: "none" }}>info@creatovate.biz</a>
        </div>
      </div>
    </div>
  );
}
